import type React from "react";
import styled from "styled-components";
import { LanguageToggle } from "../components/LanguageToggle";
import { useLanguage } from "../contexts/LanguageContext";

const PageContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px 0;
`;

const ContentWrapper = styled.div`
    width: 60vw;
    max-width: 900px;
    min-width: 600px;
`;

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
    width: 100%;
`;

const AcknowledgementContent = styled.div`
    line-height: 1.8;
    font-size: var(--size-main);
    color: var(--color-dark-green);
    margin-bottom: 3rem;
    
    p {
        margin-bottom: 1.5rem;
    }
`;

const PartnersSection = styled.div`
    margin-top: 3rem;
    width: 100%;
`;

const PartnersTitle = styled.h2`
    font-size: var(--size-main);
    color: var(--color-dark-green);
    margin-bottom: 2rem;
    text-align: center;
`;

const PartnersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 2rem;
    align-items: center;
    justify-items: center;
`;

const PartnerLogo = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    background: rgba(var(--color-primary-rgb), 0.05);
    border-radius: 12px;
    transition: all 0.3s ease;
    height: 120px;
    width: 100%;
    max-width: 200px;
    
    &:hover {
        background: rgba(var(--color-primary-rgb), 0.1);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    img {
        max-width: 100%;
        max-height: 80px;
        width: auto;
        height: auto;
        object-fit: contain;
        filter: brightness(0.9);
        transition: filter 0.3s ease;
    }
    
    &:hover img {
        filter: brightness(1);
    }
`;

const Acknowledgement: React.FC = () => {
  const { language } = useLanguage();

  const content = {
    en: 'DataDesc is funded and supported by the German federal government, the German state governments, and the Joint Science Conference (GWK) as part of the NFDI4ING consortium, managed by the German Research Foundation (DFG) – 442146713. This work was also supported by the Helmholtz Association as part of the program "Energy System Design".',
    de: 'DataDesc wird von der deutschen Bundesregierung, den deutschen Landesregierungen und der Gemeinsamen Wissenschaftskonferenz (GWK) im Rahmen des NFDI4ING-Konsortiums finanziert und unterstützt, verwaltet von der Deutschen Forschungsgemeinschaft (DFG) – 442146713. Diese Arbeit wurde auch von der Helmholtz-Gemeinschaft im Rahmen des Programms "Energy System Design" unterstützt.',
  };

  const partners = [
    {
      name: "NFDI4ING",
      logo: "/nfdi4ing_logo.svg",
      url: "https://nfdi4ing.de/",
      alt: "NFDI4ING Logo",
    },
    {
      name: "DFG",
      logo: "/dfg_logo.png",
      url: "https://www.dfg.de/",
      alt: "Deutsche Forschungsgemeinschaft Logo",
    },
    {
      name: "GWK",
      logo: "/gwk_logo.png",
      url: "https://www.gwk-bonn.de/",
      alt: "Gemeinsame Wissenschaftskonferenz Logo",
    },
    {
      name: "Helmholtz",
      logo: "/helmholz_logo.png",
      url: "https://www.helmholtz.de/",
      alt: "Helmholtz Association Logo",
    },
    {
      name: "Forschungszentrum Jülich",
      logo: "/juelich_logo.svg",
      url: "https://www.fz-juelich.de/de",
      alt: "Forschungszentrum Jülich Logo",
    },
  ];

  return (
    <PageContainer>
      <ContentWrapper>
        <HeaderContainer>
          <h1>Acknowledgement</h1>
          <LanguageToggle variant="light" />
        </HeaderContainer>
        <AcknowledgementContent>
          <p>{content[language]}</p>
        </AcknowledgementContent>

        <PartnersSection>
          <PartnersTitle>
            {language === "de" ? "Organisationen" : "Organizations"}
          </PartnersTitle>
          <PartnersGrid>
            {partners.map((partner) => (
              <PartnerLogo
                key={partner.name}
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                title={partner.name}
              >
                <img src={partner.logo} alt={partner.alt} />
              </PartnerLogo>
            ))}
          </PartnersGrid>
        </PartnersSection>
      </ContentWrapper>
    </PageContainer>
  );
};

export default Acknowledgement;
